import { isWeb } from '@gluestack-ui/nativewind-utils/IsWeb';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Image } from 'expo-image';
import { useLocalSearchParams, useNavigation } from 'expo-router';
import { useAtomValue } from 'jotai';
import { AlertCircleIcon } from 'lucide-react-native';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Share, ScrollView, Platform, KeyboardAvoidingView } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { twJoin } from 'tailwind-merge';
import { z } from 'zod';

import { promotional_network } from '@/assets/images';
import { Header } from '@/components/header';
import { getScreenColumnClasses } from '@/components/screenColumn';
import { Button, ButtonSpinner, ButtonText } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import {
  FormControl,
  FormControlError,
  FormControlErrorIcon,
  FormControlErrorText,
} from '@/components/ui/form-control';
import { Heading } from '@/components/ui/heading';
import { Input, InputField } from '@/components/ui/input';
import { Text } from '@/components/ui/text';
import { VStack } from '@/components/ui/vstack';
import { ANALYTICS_EVENTS } from '@/constants/analytics';
import {
  INVITE_SHARE,
  getInviteMessageWithLink,
  getInviteEmailBody,
  addUtmToInviteLink,
} from '@/constants/invites';
import { REFERRAL_BASE_URL } from '@/constants/links';
import { NAV_LABELS } from '@/constants/navigation';
import { REFERRAL_COPY, INVITE_COPY_VERSION, type ReferralLocation } from '@/constants/referrals';
import { ROUTES } from '@/constants/routes';
import useClipboard from '@/hooks/useClipboard';
import { useColors } from '@/hooks/useColors';
import { useToaster } from '@/hooks/useToaster';
import { getCurrentUserV2QueryOptions } from '@/services/getCurrentUserV2';
import { userIdAtom } from '@/services/globalState';
import { sendReferralV2 } from '@/services/sendReferralV2';
import { segmentClient } from '@/util/analytics';

const SCREEN_COLUMN = getScreenColumnClasses({
  maxWidth: 'max-w-screen-md',
  gutter: 'px-6 md:px-11',
});

/**
 * Invites screen for inviting colleagues via link sharing and email invitations.
 *
 * Features:
 * - Quick sharing via copy link and native share
 * - Email-based invitations with validation
 * - Analytics tracking for all user actions
 * - Responsive design for web and mobile
 */
export const invitesSchema = z.object({
  emails: z
    .string()
    .trim()
    .transform((data: string) => data.replaceAll(/\s+/g, ',').replaceAll(/,+/g, ',').split(','))
    .refine((data: any[]) => data.every((email) => z.string().email().safeParse(email).success), {
      message: 'Each email must be a valid email address',
    })
    .optional()
    .or(z.literal('')),
});

export default function Invites() {
  const userId = useAtomValue(userIdAtom);

  const userQueryOptions = getCurrentUserV2QueryOptions(userId);

  const { data } = useQuery(userQueryOptions);

  const toast = useToaster();

  const { source } = useLocalSearchParams<{ source?: string }>();

  const referralLocation: ReferralLocation = (() => {
    if (source === 'header') {
      return 'invites_header';
    }

    return source === 'quick_action' ? 'quick_action' : 'invites_account';
  })();

  useEffect(() => {
    segmentClient.track(ANALYTICS_EVENTS.INVITE_SCREEN_VIEWED, {
      invite_copy_version: INVITE_COPY_VERSION,
      referral_location: referralLocation,
      referral_role: 'referrer',
    });
  }, []);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isDirty, isValid, isSubmitting },
  } = useForm({
    defaultValues: {
      emails: '',
    },
    mode: 'onSubmit',
    resolver: zodResolver(invitesSchema),
  });

  const navigation = useNavigation();

  const { mutateAsync: referralMutation, isSuccess } = useMutation({
    mutationFn: sendReferralV2,
    onSuccess: (_, context) => {
      const isSingular = context?.emails.length === 1;

      toast.showSuccess({
        title: 'Success',
        message: `Success! Invite email${isSingular ? '' : 's'} sent.`,
      });

      reset();
    },
    onError: (error, variables) => {
      console.error('Invites ERROR:', error);

      const isSingular = variables?.emails.length === 1;

      segmentClient.track(ANALYTICS_EVENTS.INVITE_FAILED, {
        user_id: userId,
        user_email: data?.email,
        user_type: data?.userType,
        referral_emails: variables?.emails,
        referral_count: variables?.emails?.length || 0,
        error: error instanceof Error ? error.message : String(error),
        invite_copy_version: INVITE_COPY_VERSION,
        referral_location: referralLocation,
        referral_role: 'referrer',
        invite_channel: 'email_invite',
      });

      toast.showError({
        title: `Unable to send invite${isSingular ? '' : 's'}`,
        message: `There was an error sending the invite${isSingular ? '' : 's'}. Try again.`,
        apiError: {
          error,
        },
      });
    },
  });

  useEffect(
    () =>
      navigation.addListener('beforeRemove', () => {
        if (!isSuccess) {
          segmentClient.track(ANALYTICS_EVENTS.INVITE_SCREEN_DISMISSED, {
            invite_copy_version: INVITE_COPY_VERSION,
            referral_location: referralLocation,
            referral_role: 'referrer',
          });
        }
      }),
    [isSuccess]
  );

  const isSubmitVisuallyDisabled = !isDirty || !isValid || isSubmitting || !data;

  const onSubmit = handleSubmit(async (submittedData) => {
    segmentClient.track(ANALYTICS_EVENTS.INVITE_SUBMITTED, {
      user_id: userId,
      user_email: data?.email,
      user_type: data?.userType,
      referral_emails: submittedData?.emails,
      referral_count: submittedData?.emails?.length || 0,
      invite_copy_version: INVITE_COPY_VERSION,
      referral_location: referralLocation,
      referral_role: 'referrer',
      invite_channel: 'email_invite',
    });

    await referralMutation({
      emails: [...submittedData?.emails],
    });
  });

  const { bottom } = useSafeAreaInsets();

  const { colors } = useColors();

  const { copyToClipboard } = useClipboard();

  const referralLink = `${REFERRAL_BASE_URL}${data?.referralCode}`;

  const onCopy = () => {
    const utmLink = addUtmToInviteLink(referralLink, 'copy_link');

    copyToClipboard(utmLink, () => {
      segmentClient.track(ANALYTICS_EVENTS.INVITE_LINK_COPIED, {
        user_name: data?.firstName && data?.lastName ? `${data.firstName} ${data.lastName}` : null,
        invite_copy_version: INVITE_COPY_VERSION,
        referral_location: referralLocation,
        referral_role: 'referrer',
        invite_channel: 'copy_link',
      });
    });
  };

  const handleShare = async () => {
    try {
      if (isWeb) {
        await handleWebShare();
      } else {
        await handleNativeShare();
      }

      segmentClient.track(ANALYTICS_EVENTS.INVITE_NATIVE_SHARED, {
        user_name: data?.firstName && data?.lastName ? `${data.firstName} ${data.lastName}` : null,
        invite_copy_version: INVITE_COPY_VERSION,
        referral_location: referralLocation,
        referral_role: 'referrer',
        invite_channel: 'share_link',
      });
    } catch (error) {
      if (error instanceof Error && error.message === 'Share canceled') {
        return; // User canceled - normal behavior
      }

      console.error('invites:share:error', error);

      toast.showError({
        title: 'Unable to share the link',
      });
    }
  };

  const handleWebShare = async () => {
    const utmLink = addUtmToInviteLink(referralLink, 'share_link');

    if (navigator.share) {
      await navigator.share({
        title: INVITE_SHARE.TITLE,
        text: `${INVITE_SHARE.MESSAGE}\n\n${utmLink}`,
      });

      return;
    }

    // Fallback to mailto
    const emailBody = `${INVITE_SHARE.MESSAGE}\n\n${utmLink}`;

    const mailto = `mailto:?subject=${encodeURIComponent(INVITE_SHARE.TITLE)}&body=${encodeURIComponent(emailBody)}`;

    if (typeof window !== 'undefined') {
      window.location.href = mailto;
    }
  };

  const handleNativeShare = async () => {
    const utmLink = addUtmToInviteLink(referralLink, 'share_link');

    await Share.share({
      message: getInviteMessageWithLink(utmLink),
      title: INVITE_SHARE.TITLE,
    });
  };

  return (
    <VStack className={twJoin('flex-1', SCREEN_COLUMN.frame)}>
      <Header
        isBackButton
        contentScrollsUnderHeader
        title={NAV_LABELS.invites.mobileTitle}
        statusBarBackgroundColor={colors.background[0]}
        backRoute={ROUTES.ACCOUNT}
      />

      <KeyboardAvoidingView
        behavior={Platform.select({ ios: 'padding', android: undefined })}
        keyboardVerticalOffset={0}
        className="flex-1">
        <ScrollView
          contentInsetAdjustmentBehavior="automatic"
          showsVerticalScrollIndicator={false}
          keyboardDismissMode="on-drag"
          keyboardShouldPersistTaps="handled"
          contentContainerClassName={SCREEN_COLUMN.content}
          contentContainerStyle={{
            flexGrow: 1,
            paddingTop: 0,
            paddingBottom: bottom + 100,
            alignItems: 'center',
          }}>
          <Image
            alt="referral graphic"
            source={promotional_network}
            contentFit="contain"
            style={{
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              height: isWeb ? ('384px' as any) : 240,
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              width: isWeb ? ('448px' as any) : 280,
            }}
            className="mx-auto"
          />

          <Heading size={isWeb ? 'h2-2xl' : 'h3-xl'} className="mb-2 text-center web:text-center">
            Grow the community, get rewarded
          </Heading>

          <Text className="mb-2 text-center text-typography-700 web:text-center">
            {REFERRAL_COPY.referrer.invitesHeroSubtitle}
          </Text>

          {/* Single card with quick share + email invite */}
          <Card size="md" variant="filled" className="w-full max-w-4xl bg-background-0 p-3 md:p-6">
            {!data?.referralCode ? (
              <VStack className="items-center justify-center gap-6 py-8 md:gap-8">
                <Text className="text-typography-500">Loading your invite link...</Text>
              </VStack>
            ) : (
              <VStack className="gap-3 md:gap-8">
                {/* Quick Share */}
                <VStack space="sm" className="w-full">
                  <Heading size="h4-md md:h3-lg" bold={false}>
                    Share your invite link
                  </Heading>

                  {/* Buttons: secondary actions */}
                  <VStack className="w-full flex-row gap-2">
                    <Button size="md" onPress={onCopy} className="flex-1 flex-row gap-2 sm:w-auto">
                      <ButtonText>Copy Link</ButtonText>
                    </Button>

                    <Button
                      size="md"
                      onPress={handleShare}
                      className="flex-1 flex-row gap-2 sm:w-auto">
                      <ButtonText>Share Link</ButtonText>
                    </Button>
                  </VStack>
                </VStack>

                {/* Invite by email */}
                <VStack className="w-full gap-2">
                  <Heading size="h4-md md:h3-lg" bold={false}>
                    Invite by email
                  </Heading>
                  <Controller
                    name="emails"
                    control={control}
                    render={({ field: { onChange, value } }) => {
                      return (
                        <FormControl isInvalid={!!errors?.emails?.message}>
                          <Input>
                            <InputField
                              autoCorrect={false}
                              style={{ color: colors.typography[900] }}
                              value={value}
                              placeholder="name@example.com, provider@hospital.example"
                              onChangeText={(text) => onChange(text.toLowerCase())}
                              autoCapitalize="none"
                              onSubmitEditing={onSubmit}
                              returnKeyType="send"
                            />
                          </Input>

                          <FormControlError>
                            <FormControlErrorIcon as={AlertCircleIcon} />
                            <FormControlErrorText>{errors?.emails?.message}</FormControlErrorText>
                          </FormControlError>
                        </FormControl>
                      );
                    }}
                  />

                  <Button
                    size="md"
                    className={twJoin('mt-4 w-full', isSubmitVisuallyDisabled && 'opacity-50')}
                    disabled={isSubmitting || !data}
                    onPress={onSubmit}>
                    {isSubmitting && <ButtonSpinner />}
                    <ButtonText>Send Invitation</ButtonText>
                  </Button>

                  {/* Email Preview */}
                  <VStack space="sm" className="w-full pt-4">
                    <Heading size="h4-md md:h3-lg" bold={false}>
                      Email Preview
                    </Heading>
                    <Card
                      variant="outline"
                      size="sm"
                      className="border-outline-200 bg-background-50">
                      <Text className="whitespace-pre-line text-sm leading-relaxed text-typography-700">
                        {getInviteEmailBody(
                          data?.lastName || 'Colleague',
                          addUtmToInviteLink(referralLink, 'email_invite')
                        )}
                      </Text>
                    </Card>
                  </VStack>
                </VStack>
              </VStack>
            )}
          </Card>
        </ScrollView>
      </KeyboardAvoidingView>
    </VStack>
  );
}
